// STRATUL 3D AL INSTALAȚIILOR: un grup gata construit, pus în orice machetă
// care îl primește (azi `plan/Randare3D`, nivelul în 3D) și scos din ea fără
// să-i atingă restul scenei — ca stratul orașului (`viewer/oras/strat3d.ts`).
//
// Țevile sunt câte un `InstancedMesh` de cilindru unitate PE SISTEM, ca
// armătura (`structure/armatura3d.ts`): un singur desen pe sistem, oricâte
// tronsoane. Canalele dreptunghiulare și echipamentele sunt cutii instanțiate,
// ca pereții din `plan/Randare3D`. Geometria stă în cadrul documentului (+y
// sud); macheta gazdă o pune în `lume`, care face întoarcerea (ui/CLAUDE.md).

import * as THREE from "three";
import {
  CULOARE, CULOARE_LIPSA, PIESE_SANITARE, culoareSegment, decalaje, pieseFaraRacord, stratul,
  type Echipament, type ModelInstalatii, type Segment, type Strat,
} from "./model";

/** Raza minimă desenată: o țeavă DN 16 are 1 cm, adică sub un pixel la scara
 *  unui nivel. Legenda o spune „îngroșat pentru vizibilitate”. */
export const RAZA_MIN_M = 0.012;
/** Un cablu se desenează cât tubul lui, dar nu sub 8 mm rază. */
const RAZA_MIN_CABLU_M = 0.008;
/** În șapă, țeava se desenează mai deschis: e îngropată, nu aparentă. */
const DESCHIS_SAPA = 0.45;
const CULOARE_ALES = 0xffc107;
/** Patul de cabluri: suportul, gri — cablurile de pe el au culoarea lor. */
const CULOARE_PAT = 0x9a948c;

export interface OptiuniStrat {
  /** Doar nivelurile date (și coloanele care trec prin ele), cu tot ce e
   *  între `zJos` și `zSus`; fără ele, toată clădirea. */
  niveluri?: ReadonlySet<string>;
  /** Corpurile nivelurilor date: coloana n-are nivelul curent, deci numai
   *  corpul ei spune dacă e a planului desenat sau a unui corp de alături. */
  corpuri?: ReadonlySet<string>;
  zJos?: number;
  zSus?: number;
  /** Cota care ajunge la z = 0 în machetă (pardoseala nivelului desenat). */
  zBaza?: number;
  /** Cota fiecărui nivel (id → m), pentru contururile apartamentelor de pe
   *  toate nivelurile (macheta clădirii); fără ea, contururile stau la `zBaza`. */
  cote?: ReadonlyMap<string, number>;
}

export interface StratInstalatii {
  grup: THREE.Group;
  /** Aprinde și stinge straturile (sistemele, tipurile de circuit) fără reconstrucție. */
  arata(vizibile: ReadonlySet<Strat>): void;
  /** Colorează elementele alese (segmente, echipamente). */
  evidentiaza(ids: ReadonlySet<string>): void;
  /** Estompează tot ce nu e în `ids` (instalațiile altor apartamente); `null`
   *  le redă culorile. */
  estompeaza(ids: ReadonlySet<string> | null): void;
  /** Aprinde și stinge contururile apartamentelor. */
  arataApartamente(aprinse: boolean): void;
  /** Id-ul elementului de sub rază, sau `null`. */
  alege(lovit: THREE.Intersection): string | null;
  /** Id-ul țevii vizibile cea mai apropiată de cameră care trece la cel mult
   *  `toleranta_m` de rază (raza în cadrul documentului): o țeavă de 2 cm are
   *  sub un pixel la scara nivelului, iar o rază exactă ar trece pe lângă ea. */
  alegeAproape(raza: THREE.Ray, toleranta_m: number): string | null;
  elibereaza(): void;
}

interface Piesa { id: string; a: THREE.Vector3; b: THREE.Vector3; r: number; culoare: THREE.Color;
                  l?: number; h?: number; directie?: [number, number] }
interface Cutie { id: string; c: THREE.Vector3; dim: [number, number, number]; deg: number;
                  culoare: THREE.Color }

/** Bucățile segmentului din fereastra de cote, coborâte la `zBaza`: o
 *  coloană care trece prin nivel se arată doar cât stă în el. Un cablu
 *  paralel cu altele pe același perete stă deplasat în șirul tuburilor
 *  (`decalaje`), picior cu picior. */
function decupeaza(s: Segment, o: OptiuniStrat, dec?: [number, number][]): THREE.Vector3[][] {
  const z0 = o.zJos ?? -Infinity, z1 = o.zSus ?? Infinity, baza = o.zBaza ?? 0;
  const out: THREE.Vector3[][] = [];
  for (let i = 1; i < s.puncte.length; i++) {
    const [ox, oy] = dec?.[i - 1] ?? [0, 0];
    const a: [number, number, number] = [s.puncte[i - 1][0] + ox, s.puncte[i - 1][1] + oy, s.puncte[i - 1][2]];
    const b: [number, number, number] = [s.puncte[i][0] + ox, s.puncte[i][1] + oy, s.puncte[i][2]];
    const dz = b[2] - a[2];
    let k0 = 0, k1 = 1;
    if (Math.abs(dz) < 1e-9) {
      if (a[2] < z0 || a[2] > z1) continue;
    } else {
      const ka = (z0 - a[2]) / dz, kb = (z1 - a[2]) / dz;
      k0 = Math.max(0, Math.min(ka, kb));
      k1 = Math.min(1, Math.max(ka, kb));
      if (k1 <= k0) continue;
    }
    const v = (k: number) => new THREE.Vector3(a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k,
                                               a[2] + dz * k - baza);
    out.push([v(k0), v(k1)]);
  }
  return out;
}

export function construiesteStrat(m: ModelInstalatii, o: OptiuniStrat = {}): StratInstalatii {
  const grup = new THREE.Group();
  grup.name = "instalatii";
  const deArs: { dispose(): void }[] = [];
  /** Un grup pe strat (sistem sau tip de circuit); echipamentele stau pe
   *  sistemul lor și se văd cât e aprins vreun strat al lui. */
  const grupuri = new Map<string, THREE.Group>();
  const plase: { mesh: THREE.InstancedMesh; ids: string[]; culori: THREE.Color[] }[] = [];

  // coloanele au nivelul de jos: se văd pe orice nivel prin care trec, tăiate
  const vertical = (s: Segment) => s.puncte.length === 2
    && Math.hypot(s.puncte[0][0] - s.puncte[1][0], s.puncte[0][1] - s.puncte[1][1]) < 1e-6;
  const alCorpului = (s: Segment) => !o.corpuri || (!!s.building && o.corpuri.has(s.building));
  const vede = (s: Segment) => !o.niveluri || (!!s.storey && o.niveluri.has(s.storey))
    || (vertical(s) && alCorpului(s));

  const tevi = new Map<Strat, Piesa[]>();
  /** Fitingurile (coturile, ramificațiile): o sferă pe vârful lor, puțin mai
   *  mare decât țeava, în culoarea ei închisă; clicul pe ea alege țeava. */
  const noduri = new Map<Strat, { id: string; c: THREE.Vector3; r: number; culoare: THREE.Color }[]>();
  const cutii = new Map<string, Cutie[]>();
  const dec = decalaje(m.segmente);
  /** Ghenele propuse, translucide: înăuntru stă coloana, care trebuie să se vadă. */
  const ghene: Cutie[] = [];
  for (const s of m.segmente) {
    // martorul din documentele vechi nu e o țeavă: piesa lui se arată roșie
    if (!vede(s) || s.rol === "lipsa") continue;
    const culoare = new THREE.Color(s.rol === "pat" ? CULOARE_PAT : culoareSegment(s));
    if (s.mediu === "sapa") culoare.lerp(new THREE.Color(0xffffff), DESCHIS_SAPA);
    const strat = stratul(s);
    const r = s.sistem === "electrice" ? Math.max(s.d_m / 2, RAZA_MIN_CABLU_M) : Math.max(s.d_m / 2, RAZA_MIN_M);
    for (const [a, b] of decupeaza(s, o, dec.get(s.id))) {
      // țeava din șapă stă la cota ei, SUB pardoseală: pardoseala machetei e
      // translucidă pe pagina Instalații, deci se vede prin ea, mai deschisă
      // (ca punctatul din plan) — nu pe fața pardoselii, pe unde nu trece
      if (a.distanceTo(b) < 1e-4) continue;
      const lista = tevi.get(strat) ?? [];
      lista.push({ id: s.id, a, b, r, culoare, l: s.sectiune?.l, h: s.sectiune?.h, directie: s.directie });
      tevi.set(strat, lista);
    }
    for (const f of s.fitinguri ?? []) {
      const p = s.puncte[f.i];
      if (!p || p[2] < (o.zJos ?? -Infinity) || p[2] > (o.zSus ?? Infinity)) continue;
      const lista = noduri.get(strat) ?? [];
      lista.push({ id: s.id, c: new THREE.Vector3(p[0], p[1], p[2] - (o.zBaza ?? 0)), r: r * 1.5,
                   culoare: culoare.clone().lerp(new THREE.Color(0x000000), 0.3) });
      noduri.set(strat, lista);
    }
  }
  const baza = o.zBaza ?? 0;
  for (const e of m.echipamente) {
    if (o.niveluri && !(e.storey && o.niveluri.has(e.storey))) continue;
    const cu: Cutie = { id: e.id, c: new THREE.Vector3(e.centru[0], e.centru[1], e.centru[2] + e.dim[2] / 2 - baza),
                        dim: e.dim, deg: e.deg ?? 0, culoare: culoareEchipament(e) };
    if (e.fel === "ghena_propusa") { ghene.push(cu); continue; }
    cutii.set(e.sistem, [...(cutii.get(e.sistem) ?? []), cu]);
  }

  const cilindru = new THREE.CylinderGeometry(1, 1, 1, 10, 1, false);
  const cub = new THREE.BoxGeometry(1, 1, 1);
  const sfera = new THREE.SphereGeometry(1, 10, 8);
  deArs.push(cilindru, cub, sfera);
  const material = () => {
    const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.45, metalness: 0.1 });
    deArs.push(mat);
    return mat;
  };
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3();
  const sus = new THREE.Vector3(0, 1, 0), dir = new THREE.Vector3(), mij = new THREE.Vector3();

  const straturi = new Set<string>([...tevi.keys(), ...cutii.keys(), ...noduri.keys()]);
  for (const sistem of straturi) {
    const g = new THREE.Group();
    g.name = `instalatii-${sistem}`;
    g.userData.sistem = sistem;
    grup.add(g);
    grupuri.set(sistem, g);
    const rotunde = (tevi.get(sistem as Strat) ?? []).filter((p) => p.l == null);
    const drepte = (tevi.get(sistem as Strat) ?? []).filter((p) => p.l != null);
    if (rotunde.length) {
      const mesh = new THREE.InstancedMesh(cilindru, material(), rotunde.length);
      rotunde.forEach((p, i) => {
        dir.subVectors(p.b, p.a);
        const L = dir.length();
        mij.addVectors(p.a, p.b).multiplyScalar(0.5);
        q.setFromUnitVectors(sus, dir.normalize());
        mesh.setMatrixAt(i, m4.compose(mij, q, sc.set(p.r, L, p.r)));
        mesh.setColorAt(i, p.culoare);
      });
      fixeaza(mesh);
      g.add(mesh);
      plase.push({ mesh, ids: rotunde.map((p) => p.id), culori: rotunde.map((p) => p.culoare) });
    }
    const fitinguri = noduri.get(sistem as Strat) ?? [];
    if (fitinguri.length) {
      const mesh = new THREE.InstancedMesh(sfera, material(), fitinguri.length);
      fitinguri.forEach((f, i) => {
        mesh.setMatrixAt(i, m4.compose(f.c, q.identity(), sc.set(f.r, f.r, f.r)));
        mesh.setColorAt(i, f.culoare);
      });
      fixeaza(mesh);
      mesh.name = "fitinguri";
      g.add(mesh);
      plase.push({ mesh, ids: fitinguri.map((f) => f.id), culori: fitinguri.map((f) => f.culoare) });
    }
    const box = [
      ...drepte.map((p) => {
        dir.subVectors(p.b, p.a);
        const L = dir.length();
        const q = new THREE.Quaternion().setFromUnitVectors(sus, dir.clone().normalize());
        // un picior vertical cu secțiune (patul din camera tehnică, cel de
        // deasupra firidei) stă cu lățimea de-a lungul peretelui (`directie`)
        if (p.directie && Math.hypot(dir.x, dir.y) < 1e-6) {
          q.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1),
                                                                 Math.atan2(p.directie[1], p.directie[0])));
        }
        return { id: p.id, c: p.a.clone().add(p.b).multiplyScalar(0.5), culoare: p.culoare, q,
                 s: new THREE.Vector3(p.l!, L, p.h!) };
      }),
      ...(cutii.get(sistem) ?? []).map((cu) => ({
        id: cu.id, c: cu.c, culoare: cu.culoare,
        q: new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), (cu.deg * Math.PI) / 180),
        s: new THREE.Vector3(cu.dim[0], cu.dim[1], cu.dim[2]),
      })),
    ];
    if (box.length) {
      const mesh = new THREE.InstancedMesh(cub, material(), box.length);
      box.forEach((b, i) => {
        mesh.setMatrixAt(i, m4.compose(b.c, b.q, b.s));
        mesh.setColorAt(i, b.culoare);
      });
      fixeaza(mesh);
      g.add(mesh);
      plase.push({ mesh, ids: box.map((b) => b.id), culori: box.map((b) => b.culoare) });
    }
  }

  if (ghene.length) {
    const mat = new THREE.MeshStandardMaterial({ color: 0xc9a27a, transparent: true, opacity: 0.25,
                                                 depthWrite: false, roughness: 0.9 });
    deArs.push(mat);
    const mesh = new THREE.InstancedMesh(cub, mat, ghene.length);
    ghene.forEach((cu, i) => mesh.setMatrixAt(i, m4.compose(
      cu.c, new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), (cu.deg * Math.PI) / 180),
      new THREE.Vector3(...cu.dim))));
    fixeaza(mesh);
    mesh.renderOrder = 2;   // după opace, ca transparența să nu ascundă țevile
    let g = grupuri.get("canalizare");
    if (!g) {
      g = new THREE.Group();
      g.name = "instalatii-canalizare";
      grup.add(g);
      grupuri.set("canalizare", g);
    }
    g.add(mesh);
    plase.push({ mesh, ids: ghene.map((cu) => cu.id), culori: ghene.map((cu) => cu.culoare) });
  }

  // PIESELE SANITARE ale arhitecturii (nu ale motorului): cutii deschise la
  // culoare, translucide, pe pardoseala nivelului, ca racordul din dreptul
  // fiecăreia să se vadă intrând în ea, nu oprindu-se în aer. Cea fără
  // scurgere sau fără apă (`INS.SAN.RACORD`) e ROȘIE: lipsa se vede pe piesă,
  // nu printr-o țeavă inventată. Nu se aleg și nu se sting cu sistemele: sunt
  // reperul lor, nu o instalație.
  const piese = m.piese.filter((p) => !o.niveluri || (!!p.storey && o.niveluri.has(p.storey)));
  if (piese.length) {
    const fara = pieseFaraRacord(m);
    const alb = new THREE.Color(0xf1efe9), rosu = new THREE.Color(CULOARE_LIPSA);
    const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: 0.6,
                                                 depthWrite: false, roughness: 0.85 });
    deArs.push(mat);
    const mesh = new THREE.InstancedMesh(cub, mat, piese.length);
    piese.forEach((p, i) => {
      const h = PIESE_SANITARE[p.kind] ?? 0.5;
      mesh.setMatrixAt(i, m4.compose(
        new THREE.Vector3(p.centru[0], p.centru[1], h / 2),
        new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), (p.deg * Math.PI) / 180),
        new THREE.Vector3(p.dim[0], p.dim[1], h)));
      mesh.setColorAt(i, fara.has(p.id) ? rosu : alb);
    });
    fixeaza(mesh);
    mesh.renderOrder = 1;   // după opace, înaintea ghenelor
    const g = new THREE.Group();
    g.name = "instalatii-piese";
    g.add(mesh);
    grup.add(g);
  }

  // contururile apartamentelor, pe pardoseala nivelului lor: fiecare
  // apartament se citește separat, de la racordul lui la ultima piesă
  const contururi = new THREE.Group();
  contururi.name = "instalatii-apartamente";
  {
    const mat = new THREE.LineBasicMaterial({ color: 0x5c6bc0, transparent: true, opacity: 0.8 });
    deArs.push(mat);
    for (const a of m.apartamente ?? []) {
      if (o.niveluri && !(a.storey && o.niveluri.has(a.storey))) continue;
      const z = ((a.storey ? o.cote?.get(a.storey) : undefined) ?? baza) - baza + 0.02;
      const geo = new THREE.BufferGeometry().setFromPoints(a.poly.map((p) => new THREE.Vector3(p[0], p[1], z)));
      deArs.push(geo);
      const linie = new THREE.LineLoop(geo, mat);
      linie.name = a.id;
      contururi.add(linie);
    }
  }
  grup.add(contururi);

  const ales = new THREE.Color(CULOARE_ALES);
  const stins = new THREE.Color(0xd9d6d0);
  let aprinse: ReadonlySet<string> = new Set();
  let pastrate: ReadonlySet<string> | null = null;
  /** Culoarea fiecărei instanțe: aleasă, estompată sau a ei. */
  const recoloreaza = () => {
    for (const p of plase) {
      p.ids.forEach((id, i) => p.mesh.setColorAt(i, aprinse.has(id) ? ales
        : pastrate && !pastrate.has(id) ? stins : p.culori[i]));
      if (p.mesh.instanceColor) p.mesh.instanceColor.needsUpdate = true;
    }
  };
  return {
    grup,
    arata: (vizibile) => {
      // un strat se vede dacă e aprins; un sistem (echipamentele lui), cât e
      // aprins el sau vreun strat al lui (`electrice.prize` aprinde tabloul)
      for (const [s, g] of grupuri) {
        const sistem = s.split(".")[0];
        g.visible = [...vizibile].some((x) => x === s || x === sistem || x.startsWith(`${s}.`));
      }
    },
    evidentiaza: (ids) => { aprinse = ids; recoloreaza(); },
    estompeaza: (ids) => { pastrate = ids; recoloreaza(); },
    arataApartamente: (on) => { contururi.visible = on; },
    alege: (lovit) => {
      const p = plase.find((x) => x.mesh === lovit.object);
      if (!p || lovit.instanceId == null) return null;
      // Raycaster-ul nu ține seama de `visible`: un sistem stins nu se alege
      let o: THREE.Object3D | null = p.mesh;
      while (o) { if (!o.visible) return null; o = o.parent; }
      return p.ids[lovit.instanceId] ?? null;
    },
    alegeAproape: (raza, toleranta_m) => {
      let id: string | null = null, celMaiAproape = Infinity;
      const peRaza = new THREE.Vector3();
      for (const [sistem, lista] of tevi) {
        if (!grup.visible || !grupuri.get(sistem)?.visible) continue;
        for (const p of lista) {
          const tol = p.r + toleranta_m;
          if (raza.distanceSqToSegment(p.a, p.b, peRaza) > tol * tol) continue;
          const t = peRaza.distanceTo(raza.origin);
          if (t < celMaiAproape) { celMaiAproape = t; id = p.id; }
        }
      }
      return id;
    },
    elibereaza: () => {
      grup.removeFromParent();
      for (const p of plase) p.mesh.dispose();
      deArs.forEach((x) => x.dispose());
    },
  };
}

/** După `setMatrixAt` sfera de încadrare e a geometriei unitate: recalculată,
 *  altfel instanțele de departe ar dispărea la marginea ecranului. */
function fixeaza(mesh: THREE.InstancedMesh): void {
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.computeBoundingSphere();
  mesh.castShadow = false;
  mesh.receiveShadow = false;
}

function culoareEchipament(e: Echipament): THREE.Color {
  if (e.fel === "ghena_propusa") return new THREE.Color(0xc9a27a);
  // doza de derivație: plină, în culoarea sistemului, ca să se vadă pe perete
  if (e.fel === "doza") return new THREE.Color(CULOARE[e.sistem]);
  return new THREE.Color(CULOARE[e.sistem]).lerp(new THREE.Color(0xffffff), 0.2);
}
